import React, { useState, useEffect, useContext } from 'react';
import { motion } from 'framer-motion';
import MapView from '../../maps/MapView';
import { ReportContext } from '../ReportContext';
import {
  Grid,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Box,
  Paper
} from '@mui/material';

const Issues = () => {
  const { issues } = useContext(ReportContext);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [categoryFilter, setCategoryFilter] = useState('All Categories');
  const [filteredIssues, setFilteredIssues] = useState([]);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);

  // Collect filter options
  useEffect(() => {
    const statuses = new Set();
    const categories = new Set();

    Array.isArray(issues) &&
      issues.forEach((issue) => {
        if (issue.status) statuses.add(issue.status);
        if (issue.category) categories.add(issue.category);
      });

    setAvailableStatuses(['All Status', ...Array.from(statuses)]);
    setAvailableCategories(['All Categories', ...Array.from(categories)]);
  }, [issues]);

  // Apply filters
  useEffect(() => {
    const term = searchTerm.toLowerCase();

    const filtered =
      Array.isArray(issues) &&
      issues.filter((issue) => {
        const matchesSearch =
          issue?.issue_id?.toLowerCase().includes(term) ||
          issue?.location?.toLowerCase().includes(term) ||
          issue?.status?.toLowerCase().includes(term) ||
          issue?.solution?.status?.toLowerCase().includes(term) ||
          issue?.category?.toLowerCase().includes(term) ||
          (Array.isArray(issue?.reports) &&
            issue.reports.some((report) =>
              report?.report_id?.toString()?.toLowerCase().includes(term)
            ));

        const matchesStatus =
          statusFilter === 'All Status' || issue?.status === statusFilter;

        const matchesCategory =
          categoryFilter === 'All Categories' ||
          issue?.category === categoryFilter;

        return matchesSearch && matchesStatus && matchesCategory;
      });

    setFilteredIssues(filtered);
  }, [issues, searchTerm, statusFilter, categoryFilter]);

  return (
    <Box sx={{ minHeight: '100vh', py: 1,}}>
      {/* Title */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        <Typography
          variant="h5"
          align="center"
          sx={{ fontWeight: 'bold', color: '#fff', mb: 3 }}
        >
          Report Tracker
        </Typography>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.5 }}
      >
        <Paper
          elevation={6}
          sx={{
            bgcolor: 'rgba(0,128,0,0.25)', // transparent greenish
            backdropFilter: 'blur(6px)',
            borderRadius: 2,
            px: 2,
            py: 1.5,
            // mb: 3,
            mx: { xs: 2, md: 'auto' },
            maxWidth: 900,
          }}
        >
          <Grid container spacing={1} display={"flex"} alignItems="center">
            <Grid item xs={12} md={6} lg={4}>
              <TextField
                fullWidth
                size="small"
                type="search"
                variant="outlined"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputLabelProps={{ style: { color: '#ccc' } }}
                InputProps={{ style: { color: '#fff', fontSize: '0.85rem' } }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#666' },
                    '&:hover fieldset': { borderColor: '#aaa' },
                    '&.Mui-focused fieldset': { borderColor: '#00bcd4' },
                  },
                }}
              />
            </Grid>

            <Grid item xs={6} md={3} lg={4}>
              <FormControl fullWidth size="small">
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={{
                    fontSize: '0.85rem',
                    color: '#fff',
                    '.MuiOutlinedInput-notchedOutline': {
                      borderColor: '#666',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#aaa',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#00bcd4',
                    },
                  }}
                >
                  {availableStatuses.map((status, idx) => (
                    <MenuItem key={idx} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6} md={3} lg={4}>
              <FormControl fullWidth size="small">
                <Select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  sx={{
                    fontSize: '0.85rem',
                    color: '#fff',
                    '.MuiOutlinedInput-notchedOutline': {
                      borderColor: '#666',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#aaa',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#00bcd4',
                    },
                  }}
                >
                  {availableCategories.map((cat, idx) => (
                    <MenuItem key={idx} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
      </motion.div>

      {/* Map */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.6 }}
      >
        <Box sx={{ height: '75vh', px: { xs: 2, md: 4 } }}>
          {Array.isArray(filteredIssues) && (
            <MapView coordinates={filteredIssues} />
          )}
        </Box>
      </motion.div>
    </Box>
  );
};

export default Issues;
